import { ICorrespondentSearchInputModel, IUpdateSolicitationInputModel } from 'inputModels';
import { serialize } from 'object-to-formdata';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { toPattern } from 'vanilla-masker';
import {
  ICorrespondentResultViewModel,
  IRequestsSubmmitedViewModel,
  ISolicitationViewModel,
} from 'viewModels';
import { v4 as uuid } from 'uuid';
import loading from '../../assets/gif/loading.gif';
import { Attachment, Button, Input, NavHeader, InteractiveMap } from '../../components';
import { CardUser } from '../../components/Cards/CardUser';
import { MASK_PROCESS_NUMBER, UF_NAMES_BY_CODE } from '../../constants';
import { useApp, useAuth } from '../../context';
import { useSolicitation } from '../../context/solicitation';
import { SolicitationController } from '../../controller/SolicitationController';
import { useAlert } from '../../hooks/useAlert';
import {
  AudienceType,
  labelAudienceType,
  RequestsSubmittedStatus,
  ServiceStatus,
  SolicitationCancelationStatus,
  TypeCardUser,
  TypeUser,
} from '../../types/enums';
import { Body, Container, ContainerProviders, Content, HeaderProvider, Providers } from './styles';
import { Position } from '../../components/InteractiveMap';
import { doubleCurrencyMask } from '../../utils/maskMoney';

export function ApprovalSolicitation() {
  const app = useApp();
  const { user } = useAuth();
  const alert = useAlert();
  const location = useLocation();
  const navigate = useNavigate();
  const { setActiveSolicitationId, setSolicitations } = useSolicitation();
  const solicitationId = location.state || location.pathname.split('/')[2];
  const [isSendingProposal, setIsSendingProposal] = useState(false);
  const [loadingCorrespondents, setLoadingCorrespondents] = useState(false);
  const [solicitation, setSolicitation] = useState<ISolicitationViewModel>();
  const [selectedCorrespondents, setSelectedCorrespondents] = useState<
    ICorrespondentResultViewModel[]
  >([]);
  const [correspondents, setCorrespondents] = useState<ICorrespondentResultViewModel[]>([]);
  const [initialPosition, setInitialPosition] = useState<Position>();
  const [correspondentsWithAddress, setCorrespondentsWithAddress] = useState<
    ICorrespondentResultViewModel[]
  >([]);
  const { handleSubmit, reset, control, watch } = useForm({
    reValidateMode: 'onChange',
    defaultValues: useMemo(
      () => ({
        nameCorrespondent: '',
        solicitationId,
        isCorrespondent: user.type === TypeUser.USER,
      }),
      [solicitationId, user.type],
    ),
  });

  const getAudienceTypeName = (audienceType: AudienceType) =>
    labelAudienceType[audienceType] ?? 'Não informado';

  useEffect(() => {
    if (solicitation?.requestsSubmitted !== undefined) {
      if (solicitation.id)
        handleFilter({
          term: nameCorrespondent,
          solicitationId: solicitation.id,
          isCorrespondent: user.type === TypeUser.USER,
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitation?.requestsSubmitted]);

  const nameCorrespondent = watch('nameCorrespondent');

  const handleFilter = useCallback(
    (filter: ICorrespondentSearchInputModel) => {
      setLoadingCorrespondents(true);
      SolicitationController.getCorrespondents({ ...filter })
        .then((correspondentsResponse) => {
          const authenticatedUserIdx = correspondentsResponse.findIndex((u) => u.id === user.id);
          if (authenticatedUserIdx !== -1) {
            const [userFinded] = correspondentsResponse.splice(authenticatedUserIdx, 1);
            correspondentsResponse.unshift(userFinded);
          }
          setCorrespondents(correspondentsResponse);
        })
        .catch(() => {
          alert.show({ message: 'Falha ao buscar correspondentes...', type: 'error' });
        })
        .finally(() => setLoadingCorrespondents(false));
    },
    [alert, user.id],
  );

  const handleToggleSelectCorrespondent = (correspondent: ICorrespondentResultViewModel) =>
    setSelectedCorrespondents((prev) =>
      prev.some((cId) => cId === correspondent)
        ? prev.filter((cId) => cId !== correspondent)
        : [...prev, correspondent],
    );

  const sendProposals = () => {
    if (!solicitationId) return;
    if (isSendingProposal) return;
    setIsSendingProposal(true);
    app.setLoading(true);
    SolicitationController.sendProposals(
      solicitationId,
      selectedCorrespondents.map((c) => c.id),
    )
      .then(() => {
        const requestsSubmitted: IRequestsSubmmitedViewModel[] = selectedCorrespondents.map((c) => {
          const request: IRequestsSubmmitedViewModel = {
            status: RequestsSubmittedStatus.PENDING,
            userId: c.id,
            user: c,
            solicitationId,
          };
          return request;
        });
        setSolicitations((prev) => {
          const idxSolicitation = prev.findIndex((d) => d.id === solicitationId);
          const newSolicitations = [...prev];
          if (idxSolicitation !== -1) {
            newSolicitations[idxSolicitation].requestsSubmitted = requestsSubmitted;
          }
          return newSolicitations;
        });
      })
      .then(() => {
        navigate('/yoursolicitations');
      })
      .catch(() => alert.show({ message: 'Falha ao enviar propostas...', type: 'error' }))
      .finally(() => {
        app.setLoading(false);
        setIsSendingProposal(false);
      });
  };

  const undoSolicitation = () => {
    if (!solicitationId) return;
    app.setLoading(true);
    const update: IUpdateSolicitationInputModel = {
      solicitationId,
      status: ServiceStatus.CANCELED,
      cancelationStatus: SolicitationCancelationStatus.UNDO_SOLICITATION,
      cancelationDetails: `Solicitação desfeita por ${user?.firstName ?? ''} 
      ${user?.lastName ?? ''}`,
    };
    const formData = serialize(update, { indices: true });
    SolicitationController.updateStatus(formData)
      .then(() => {
        setSolicitations((prev) => {
          const idxSolicitation = prev.findIndex((d) => d.id === solicitationId);
          const newSolicitations = [...prev];
          if (idxSolicitation !== -1) {
            newSolicitations[idxSolicitation].status = ServiceStatus.CANCELED;
            newSolicitations[idxSolicitation].cancelationDetails = update.cancelationDetails;
            newSolicitations[idxSolicitation].cancelationStatus =
              SolicitationCancelationStatus.UNDO_SOLICITATION;
            newSolicitations[idxSolicitation].disabledAt = new Date();
          }
          return newSolicitations;
        });
      })
      .then(() => {
        navigate('/yoursolicitations');
        alert.show({ message: 'Solicitação desfeita com sucesso!', type: 'success' });
      })
      .catch(() => {
        navigate('/yoursolicitations');
        alert.show({ message: 'Falha ao desfazer solicitação...', type: 'error' });
      })
      .finally(() => {
        app.setLoading(false);
      });
  };
  const handleSendProposals = () => {
    if (!solicitationId) return;
    setActiveSolicitationId(solicitationId);
    sendProposals();
  };

  useEffect(() => {
    if (!solicitationId) navigate('/feed');
    else {
      app.setLoading(true);
      reset({ solicitationId });
      SolicitationController.getById(solicitationId)
        .then((res) => {
          setSolicitation(res);
        })
        .catch(() => {
          alert.show({ message: 'Falha ao carregar solicitação', type: 'error' });
          navigate('/feed');
        })
        .finally(() => app.setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitationId]);

  useEffect(() => {
    setCorrespondentsWithAddress([]);
    if (correspondents.length > 0) {
      const withAddress = correspondents.filter((c) => c.address);
      setCorrespondentsWithAddress(withAddress);
    }
  }, [correspondents]);

  useEffect(() => {
    if (solicitation?.address?.latitude && solicitation?.address?.longitude) {
      setInitialPosition({
        lat: solicitation?.address?.latitude,
        lng: solicitation?.address?.longitude,
      });
    }
  }, [solicitation?.address?.latitude, solicitation?.address?.longitude]);

  return (
    <Content>
      <NavHeader typeHeader="company" />
      <Container>
        <h1>Solicitação</h1>
        {solicitation === undefined ? (
          <div className="loading">
            <img src={loading} alt="Legal Control Hub Loading" />
            <p>Carregando...</p>
          </div>
        ) : (
          <Body>
            <div className="containerSolicitationData">
              <div className="container">
                {solicitation.protocolNumber && (
                  <div className="containerClient">
                    <h1>Número de protocolo</h1>
                    <p>{solicitation.protocolNumber ?? 'Não identificado'}</p>
                  </div>
                )}
                {solicitation.client && (
                  <div className="containerClient">
                    <h1>Cliente</h1>
                    <p>{solicitation.client?.name ?? 'Não identificado'}</p>
                  </div>
                )}
                <div className="correspondeceService">
                  <h1>Tipo do Trabalho</h1>
                  <p>
                    {solicitation?.correspondence?.name ?? '-'}
                    {solicitation?.audienceType ? ',' : ''}{' '}
                    {solicitation?.audienceType
                      ? getAudienceTypeName(solicitation?.audienceType)
                      : ''}{' '}
                  </p>
                </div>
                <div className="containerApprover">
                  <h1>Comarca</h1>
                  <p>
                    {solicitation.districtName ?? ''} -
                    {solicitation.districtUfCode && UF_NAMES_BY_CODE[solicitation.districtUfCode]}
                  </p>
                </div>
                <div className="containerDateLimit">
                  <h1>Prazo</h1>
                  <div className="containerDate">
                    <p>{new Date(solicitation.dateLimit).toLocaleDateString('pt-BR')}</p>
                    <div className="contentDate">
                      <h2>Hora</h2>
                      <span>
                        {new Date(solicitation.dateLimit).getHours().toString().padStart(2, '0')}
                      </span>
                      <h2>:</h2>
                      <span>
                        {new Date(solicitation.dateLimit).getMinutes().toString().padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                  {solicitation.isVirtualAudience ? (
                    <div className="containerApprover">
                      <h1>Link da Audiência</h1>
                      <p>{solicitation.audienceLink ?? '-'}</p>
                    </div>
                  ) : undefined}
                  <div>
                    <h1>Anexos</h1>
                    {solicitation.attachments?.length === 0 ? (
                      <p>Nenhum anexo...</p>
                    ) : (
                      solicitation.attachments?.map((att) => (
                        <div className="containerMapAttachment" key={uuid()}>
                          <Attachment
                            data={{
                              id: att.id,
                              type: att.contentType ?? '',
                              name: att.name ? `${att.name}${att.extension || ''}` : 'Sem nome',
                            }}
                            options={{
                              disableDelete: true,
                            }}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <div className="containerData">
                <div className="containerApprover">
                  <h1>Processo</h1>
                  <p>
                    {solicitation.processNumber
                      ? toPattern(solicitation.processNumber, MASK_PROCESS_NUMBER)
                      : '-'}
                  </p>
                </div>
                <div className="containerApprover">
                  <h1>Especialidade</h1>
                  <p>{solicitation.specialty?.name ?? '-'}</p>
                </div>
                {solicitation.isIntegratedProtocol ? (
                  <div className="containerApprover">
                    <h1>Protocolo Integrado</h1>
                    <p>R${solicitation.integratedProtocolValue}</p>
                  </div>
                ) : undefined}
                {solicitation.folderNumber ? (
                  <div className="containerApprover">
                    <h1>Número da Pasta Interno</h1>
                    <p>{solicitation.folderNumber}</p>
                  </div>
                ) : undefined}
                {solicitation.isLawyer || solicitation.isRepresentative ? (
                  <div className="containerApprover">
                    <h1>Detalhes</h1>
                    {solicitation.isLawyer && solicitation.isRepresentative ? (
                      <p>Advogado e Preposto</p>
                    ) : solicitation.isLawyer ? (
                      <p>Advogado</p>
                    ) : (
                      <p>Preposto</p>
                    )}
                  </div>
                ) : undefined}
                <div className="containerNote">
                  <h1>Valor</h1>
                  <p>
                    R${' '}
                    {solicitation.value ? doubleCurrencyMask(solicitation.value?.toString()) : '-'}
                  </p>
                </div>
                <div className="containerNote">
                  <h1>Observações</h1>
                  <p>{solicitation.note ?? '-'}</p>
                </div>
                <div className="containerNote">
                  <h1>Vara ou Juizado</h1>
                  <p>{solicitation.court?.name}</p>
                </div>
              </div>
            </div>
            <div className="containerMap">
              {solicitation.address?.street &&
                solicitation.address?.number &&
                solicitation.address?.city &&
                solicitation.address?.uf &&
                solicitation.address?.postalCode && (
                  <div className="containerLocation">
                    <h1>Local</h1>
                    <p>{`${solicitation.address?.street}, ${solicitation.address?.number ? solicitation.address?.number : 'S/Nº'
                      }, ${solicitation.address?.city} - ${solicitation.address?.uf}, ${solicitation.address?.postalCode
                      }`}</p>
                  </div>
                )}
              {solicitation && initialPosition && (
                <InteractiveMap
                  initialPosition={initialPosition}
                  correspondents={correspondentsWithAddress}
                />
              )}
            </div>
          </Body>
        )}
      </Container>
      <ContainerProviders onSubmit={handleSubmit(handleFilter)}>
        <Providers>
          <HeaderProvider>
            <div className="providerSearch">
              {solicitation && solicitation.id && (
                <Input
                  control={control}
                  name="nameCorrespondent"
                  type="searchInput"
                  variant="standard"
                  onCallBack={() =>
                    handleFilter({
                      term: nameCorrespondent,
                      solicitationId: solicitation.id as string,
                      isCorrespondent: user.type === TypeUser.USER,
                    })
                  }
                  placeholder="Quem você está procurando"
                />
              )}
            </div>
          </HeaderProvider>
          <div className="content-selected-user">
            <div className="content-cards-users">
              <h1>Correspondentes</h1>
              <div className="content-user">
                {loadingCorrespondents ? (
                  <div className="loading">
                    <img src={loading} alt="Legal Control Hub Loading" />
                    <p>Carregando...</p>
                  </div>
                ) : correspondents.length === 0 ? (
                  <p>Nenhum correspondente encontrado...</p>
                ) : (
                  <div className='content-card'>
                    {correspondents.map((correspondent) => (
                      <CardUser
                        key={correspondent.id + uuid()}
                        onToggleSelect={() => handleToggleSelectCorrespondent(correspondent)}
                        typeUserCard={TypeCardUser.REQUEST_SERVICE}
                        currentUser={{
                          id: correspondent.id,
                          firstName: correspondent.firstName ?? '',
                          lastName: correspondent.lastName ?? '',
                          isVerified: correspondent.isVerified,
                          inscriptionNumber: correspondent.inscriptionNumber ?? '',
                          classificationType: correspondent.classificationType,
                          email: correspondent.email ?? '',
                          proviedServices: correspondent.proviedServices,
                          profileType: correspondent.profileType,
                          cell: correspondent.cell ?? '',
                          selected: selectedCorrespondents.some((c) => c.id === correspondent.id),
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="footerContent">
            <Button
              dimensions={{ width: '15rem', height: '2.2rem' }}
              background="primary"
              text="Desfazer"
              loading={isSendingProposal}
              onClick={undoSolicitation}
            />
            <div className="generateContainer">
              <Button
                text="Encaminhar proposta"
                background="primary"
                loading={isSendingProposal}
                dimensions={{ width: '15rem', height: '2.2rem' }}
                disabled={!selectedCorrespondents.length || isSendingProposal}
                onClick={handleSendProposals}
              />
              <h1>
                {selectedCorrespondents.length}{' '}
                <span>{`prestador${selectedCorrespondents.length !== 1 ? 'es' : ''} selecionado${selectedCorrespondents.length !== 1 ? 's' : ''
                  }`}</span>
              </h1>
            </div>
          </div>
        </Providers>
      </ContainerProviders>
    </Content>
  );
}
